import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RestPoliceService } from '@app/core/api/police.service';
import { ActivePoliceQuery } from '@app/core/state/active-police/active-police.query';
import { ActivePoliceState, ActivePoliceStore } from '@app/core/state/active-police/active-police.store';
import { PayloadUtil } from '@app/core/util/payload.util';
import { setProps } from '@ngneat/elf';
import { Klausel, PayloadKlauselList, PayloadPolizze, PayloadPosition, PayloadPositionResultList, PayloadVertrag, PayloadVertragResultList, Polizze, Position, VertragResult } from '@pezi/entities';
import _default from 'chart.js/dist/plugins/plugin.tooltip';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PersistentTabService } from '../persistent-tab/persistent-tab.service';
import { PersistentTabUtil } from '../persistent-tab/persistent-tab.util';
import bodyFont = _default.defaults.bodyFont;

@Injectable({providedIn: 'root'})
export class ActivePoliceService {

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private activePoliceStore: ActivePoliceStore,
              public activePoliceQuery: ActivePoliceQuery,
              private policeService: RestPoliceService,
              private snackBar: MatSnackBar, private tabService: PersistentTabService) {
  }

  async initPolice(policeId: number, verId?: number, posId?: number, reload?: boolean) {
    this.destroy$.next(true);
    this.activePoliceStore.data.update(setProps({
      policeLoading:      false,
      vertraegeLoading:   false,
      policeLoadingError: false
    }));

    const currentState = this.activePoliceStore.data.getValue();
    const policeChanged = !currentState.police || currentState.police.id !== +policeId;
    const vertragChanged = !currentState.vertrag || currentState.vertrag.id !== +verId;
    if (!policeId) {
      this.resetPoliceData();
    } else {
      if (policeChanged) {
        this.resetPoliceData();
        await this.initAllPolizzenEndpoints(policeId, reload);
      }
      if (!verId) {
        this.resetVertragData();
      } else {
        this.handleVertragPositionen(currentState, policeChanged, vertragChanged, policeId, verId, posId, reload);
      }
    }
  }

  clearStore() {
    this.activePoliceStore.data.reset();
  }

  private async initAllPolizzenEndpoints(policeId: number, reload?: boolean) {
    const cachedObs$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadPolizze>('/polizze/' + policeId, 'police');
    this.activePoliceStore.data.update(setProps({
      policeLoading: true,
      police:        null,
      vertraege:     null
    }));
    // Wait for Polizze before possible init Vertrag/Position. Otherwise this could lead to Nullpointer errors
    const payloadPol = await firstValueFrom((cachedObs$ ? cachedObs$ : this.policeService.getPoliceForId(policeId)).pipe(
      take(1),
      takeUntil(this.destroy$)
    ));
    this.initPoliceData(payloadPol);
    if (this.activePoliceStore.data.getValue().police && !this.activePoliceStore.data.getValue().police.locked) {
      const cachedVertragObs$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadVertragResultList>('/polizze/' + payloadPol.data.id, 'vertraege');
      this.activePoliceStore.data.update(setProps({
        vertraegeLoading: true
      }));
      const payloadVertrage = await firstValueFrom((cachedVertragObs$ ? cachedVertragObs$ : this.policeService.getVertraegeForPolice(payloadPol.data.id))
        .pipe(
          take(1),
          takeUntil(this.destroy$)
        ));
      this.initVertraegeForPolice(payloadPol.data, payloadVertrage);
    }
  }

  private async handleVertragPositionen(currentState: ActivePoliceState, policeChanged: boolean, vertragChanged: boolean, policeId: number, verId: number, posId?: number, reload?: boolean) {
    if (policeChanged || vertragChanged) {
      this.resetVertragData();
      await this.initAllVertragEndpoints(policeId, verId, reload);
    }
    if (!posId) {
      this.resetPositionData();
    } else {
      if (policeChanged || vertragChanged || !currentState.position || currentState.position.id !== +posId) {
        this.resetPositionData();
        await this.initAllPositionEndpoints(policeId, verId, posId, reload);
      }
    }
  }

  private async initAllPositionEndpoints(polint: number, verId: number, posId: number, reload?: boolean) {
    this.activePoliceStore.data.update(setProps({
      positionKlauselnLoading: true,
      positionLoading:         true
    }));
    if (!reload) {
      const checkObs$ = this.tabService.getCachedStateFromTabs<PayloadPosition>('/polizze/' + polint, 'position');
      const checkPos = checkObs$ ? await firstValueFrom(checkObs$) : null;
      if (checkPos?.data && (checkPos.data.id !== +posId || checkPos.data.vertragId !== +verId)) {
        reload = true; // Different Position is currently cached = reload
      }
    }
    const cachedPos$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadPosition>('/polizze/' + polint, 'position');
    const cachedKlauseln$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadKlauselList>('/polizze/' + polint, 'positionKlauseln');
    const currentPos = await firstValueFrom((cachedPos$ ? cachedPos$ : this.policeService.getPositionForVertraege(polint, verId, posId)).pipe(
      take(1),
      takeUntil(this.destroy$)
    ));
    this.initPosition(currentPos, verId, posId);

    const currentKlauseln = await firstValueFrom((cachedKlauseln$ ? cachedKlauseln$ : this.policeService.getKlauselnForPosition(polint, verId, posId)).pipe(
      take(1),
      takeUntil(this.destroy$)
    ));
    this.initKlauselnPosition(currentKlauseln, verId, posId);
  }

  private async initAllVertragEndpoints(polint: number, verId: number, reload?: boolean) {
    this.activePoliceStore.data.update(setProps({
      vertragLoading:         true,
      avbsLoading:            true,
      vertragKlauselnLoading: true,
      positionenLoading:      true
    }));
    if (!reload) {
      const checkObs$ = this.tabService.getCachedStateFromTabs<PayloadVertrag>('/polizze/' + polint, 'vertrag');
      const checkVertrag = checkObs$ ? await firstValueFrom(checkObs$) : null;
      if (checkVertrag?.data && checkVertrag.data.id !== +verId) {
        reload = true; // Different Vertrag is currently cached = reload
      }
    }
    const cachedAvbObs$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadKlauselList>('/polizze/' + polint, 'avbs');
    const cachedKlauseln$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadKlauselList>('/polizze/' + polint, 'vertragKlauseln');
    const cachedPositionenObs$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadPositionResultList>('/polizze/' + polint, 'positionen');
    const cachedVetragObs$ = reload ? null : this.tabService.getCachedStateFromTabs<PayloadVertrag>('/polizze/' + polint, 'vertrag');

    const currentVertrag = await firstValueFrom((cachedVetragObs$ ? cachedVetragObs$ : this.policeService.getVertragForPolice(polint, verId)).pipe(
      take(1),
      takeUntil(this.destroy$)
    ));
    // Initially add tab after Vertrag loaded to avoid potential wait time until everything finished
    this.initPolizzenVertrag(currentVertrag, verId);
    if (!this.activePoliceStore.data.getValue().vertragLoadingError) {
      combineLatest([
          (cachedAvbObs$ ? cachedAvbObs$ : this.policeService.getAvbForVertrag(polint, verId)),
          (cachedKlauseln$ ? cachedKlauseln$ : this.policeService.getKlauselnForVertrag(polint, verId)),
          (cachedPositionenObs$ ? cachedPositionenObs$ : this.policeService.getPositionenForVertraege(polint, verId))
        ]
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe(([avbs, klausln, positionen]) => {
          if (avbs) {
            this.initAvbVertrag(avbs);
          }
          if (klausln) {
            this.initKlauselnVertrag(klausln);
          }
          if (positionen) {
            this.initPositionenVertrag(positionen);
          }
          if (avbs && klausln && positionen) {
            this.tabService.updateTabStateProperties({
              config: PersistentTabUtil.getPolizzeVertragTabConfig(this.activePoliceStore.data.getValue().police, verId),
              data:   this.activePoliceStore.data.getValue()
            }, [
              {
                key:   'avbs',
                value: PayloadUtil.checkPayload(avbs) ? avbs.data : []
              },
              {
                key:   'vertragKlauseln',
                value: PayloadUtil.checkPayload(klausln) ? klausln.data : []
              },
              {
                key:   'positionen',
                value: PayloadUtil.checkPayload(positionen) ? positionen.data : []
              }
            ]);
          }
        });
    }
  }

  private initVertraegeForPolice(police: Polizze, payloadVertrage: PayloadVertragResultList) {
    let vertraege: VertragResult[] = [];
    if (PayloadUtil.handlePayloadSnackbarErrorOnly(payloadVertrage, this.snackBar,
      'Fehler: Verträge konnten nicht geladen werden')) {
      vertraege = payloadVertrage.data;
    }
    this.activePoliceStore.data.update(setProps({
      vertraegeLoading: false,
      vertraege
    }));
    this.tabService.updateTabStateProperties({
      config: PersistentTabUtil.getPolizzeTabConfig(police),
      data:   this.activePoliceStore.data.getValue()
    }, [
      {
        key:   'vertraege',
        value: vertraege
      }
    ]);
  }

  private initPoliceData(payloadPol: PayloadPolizze) {
    let police: Polizze;
    let error = false;
    if (PayloadUtil.handlePayloadSnackbarErrorOnly(payloadPol, this.snackBar,
      'Fehler: Polizze konnte nicht geladen werden')) {
      police = payloadPol.data;
    } else {
      error = true;
    }
    this.activePoliceStore.data.update(setProps({
      policeLoading:      false,
      police,
      policeLoadingError: error
    }));
    this.tabService.updateTabStateProperties({
      config: PersistentTabUtil.getPolizzeTabConfig(police),
      data:   this.activePoliceStore.data.getValue()
    }, [
      {
        key:   'police',
        value: police
      }
    ]);
  }

  private initPolizzenVertrag(vertragPayload: PayloadVertrag, verId: number): void {
    let error = !PayloadUtil.handlePayloadSnackbarErrorOnly(vertragPayload, this.snackBar,
      'Fehler: Vertrag konnte nicht geladen werden');
    this.activePoliceStore.data.update(setProps({
      vertragLoading:      false,
      vertrag:             !error ? vertragPayload.data : null,
      vertragLoadingError: error
    }));
    this.tabService.updateTabStateProperties({
      config: PersistentTabUtil.getPolizzeVertragTabConfig(this.activePoliceStore.data.getValue().police, verId),
      data:   this.activePoliceStore.data.getValue()
    }, [
      {
        key:   'vertrag',
        value: !error ? vertragPayload.data : null
      }
    ]);
  }

  private initKlauselnVertrag(klauselnPayload: PayloadKlauselList): void {
    this.activePoliceStore.data.update(setProps({
      vertragKlauselnLoading: false,
      vertragKlauseln:        PayloadUtil.handlePayloadSnackbarErrorOnly(klauselnPayload, this.snackBar,
        'Fehler: Klauseln konnten nicht geladen werden') ? klauselnPayload.data : []
    }));
  }

  private initAvbVertrag(avbPaylod: PayloadKlauselList): void {
    this.activePoliceStore.data.update(setProps({
      avbsLoading: false,
      avbs:        PayloadUtil.handlePayloadSnackbarErrorOnly(avbPaylod, this.snackBar,
        'Fehler: AVBs konnten nicht geladen werden') ? avbPaylod.data : []
    }));
  }

  private initPositionenVertrag(posPayload: PayloadPositionResultList): void {
    this.activePoliceStore.data.update(setProps({
      positionenLoading: false,
      positionen:        PayloadUtil.handlePayloadSnackbarErrorOnly(posPayload, this.snackBar,
        'Fehler: Positionen konnten nicht geladen werden') ? posPayload.data : []
    }));
  }

  private initPosition(posPayLoad: PayloadPosition, verId: number, posId: number): void {
    let error = !PayloadUtil.handlePayloadSnackbarErrorOnly(posPayLoad, this.snackBar,
      'Fehler: Position konnte nicht geladen werden');
    this.activePoliceStore.data.update(setProps({
      positionLoading:      false,
      position:             !error ? posPayLoad.data : null,
      positionLoadingError: error
    }));
    this.tabService.updateTabStateProperties({
      config: PersistentTabUtil.getPolizzePositionTabConfig(this.activePoliceStore.data.getValue().police, verId, posId),
      data:   this.activePoliceStore.data.getValue()
    }, [
      {
        key:   'position',
        value: !error ? posPayLoad.data : null
      }
    ]);
  }

  private initKlauselnPosition(klauselnPayload: PayloadKlauselList, verId: number, posId: number): void {
    this.activePoliceStore.data.update(setProps({
      positionKlauselnLoading: false,
      positionKlauseln:        PayloadUtil.handlePayloadSnackbarErrorOnly(klauselnPayload, this.snackBar,
        'Fehler: Klauseln konnten nicht geladen werden') ? klauselnPayload.data : []
    }));
    this.tabService.updateTabStateProperties({
      config: PersistentTabUtil.getPolizzePositionTabConfig(this.activePoliceStore.data.getValue().police, verId, posId),
      data:   this.activePoliceStore.data.getValue()
    }, [
      {
        key:   'positionKlauseln',
        value: PayloadUtil.checkPayload(klauselnPayload) ? klauselnPayload.data : []
      }
    ]);
  }

  /**
   * löscht alle Daten von police abwärts
   * Wenn sich die polizze ändert, können vertrag und position sowieso nicht mehr stimmen
   *
   * @private
   */
  private resetPoliceData() {
    this.activePoliceStore.data.reset();
  }

  /**
   * löscht alle Daten von vertrag abwärts
   * Wenn sich der Vertrag ändert, kann position sowieso nicht mehr stimmen
   *
   * @private
   */
  private resetVertragData() {
    this.activePoliceStore.data.update(setProps({
      vertragLoading:      false,
      vertragLoadingError: false,
      vertrag:             undefined,
      vertragKlauseln:     undefined,
      vertragKlauselnLoading: false,
      avbsLoading:         false,
      avbs:                undefined,
      positionenLoading:   false,
      positionen:          undefined
    }));
    this.resetPositionData();
  }

  /**
   * löscht alle Position Daten
   *
   * @private
   */
  private resetPositionData() {
    this.activePoliceStore.data.update(setProps({
      positionLoading:      false,
      positionLoadingError: false,
      position:             undefined,
      positionKlauselnLoading: false,
      positionKlauseln:     undefined
    }));
  }
}
